import api from './api';

export interface Brand {
  id: string;
  project_id: string;
  name: string;
  type: 'own' | 'competitor';
  keywords: string[];
  hashtags: string[];
  handles: {
    instagram?: string;
    twitter?: string;
    facebook?: string;
    linkedin?: string;
    youtube?: string;
    tiktok?: string;
  };
  exclude_keywords: string[];
  is_monitoring: boolean;
  created_at: string;
  updated_at: string;
}

export interface Mention {
  id: string;
  project_id: string;
  brand_id: string;
  platform: 'twitter' | 'instagram' | 'facebook' | 'linkedin' | 'youtube' | 'reddit' | 'news' | 'blog';
  external_id: string;
  url?: string;
  author_name?: string;
  author_handle?: string;
  author_avatar?: string;
  author_followers: number;
  content: string;
  media_urls: string[];
  sentiment: 'positive' | 'negative' | 'neutral';
  sentiment_score: number;
  engagement: {
    likes: number;
    comments: number;
    shares: number;
    views: number;
  };
  reach_estimate: number;
  is_influencer: boolean;
  requires_response: boolean;
  responded: boolean;
  mentioned_at: string;
  fetched_at: string;
  brand?: {
    id: string;
    name: string;
    type: 'own' | 'competitor';
  };
}

export interface ListeningStats {
  totalMentions: number;
  sentimentBreakdown: {
    positive: number;
    negative: number;
    neutral: number;
  };
  platformBreakdown: Record<string, number>;
  totalReach: number;
  influencerMentions: number;
  pendingResponses: number;
  averageSentiment: string;
}

export interface CreateBrandData {
  name: string;
  type?: 'own' | 'competitor';
  keywords?: string[];
  hashtags?: string[];
  handles?: Record<string, string>;
  exclude_keywords?: string[];
}

export interface MentionFilters {
  brand_id?: string;
  brand_type?: 'own' | 'competitor';
  platform?: string;
  sentiment?: 'positive' | 'negative' | 'neutral';
  is_influencer?: boolean;
  requires_response?: boolean;
  responded?: boolean;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// Helper function to add project header
const withProjectHeader = (projectId: string) => ({
  headers: {
    'X-Project-Id': projectId
  }
});

const getBrands = async (projectId: string) => {
  const response = await api.get('/listening/brands', withProjectHeader(projectId));
  return response.data;
};

const createBrand = async (projectId: string, data: CreateBrandData) => {
  const response = await api.post('/listening/brands', data, withProjectHeader(projectId));
  return response.data;
};

const updateBrand = async (projectId: string, brandId: string, data: Partial<CreateBrandData & { is_monitoring?: boolean }>) => {
  const response = await api.put(`/listening/brands/${brandId}`, data, withProjectHeader(projectId));
  return response.data;
};

const deleteBrand = async (projectId: string, brandId: string) => {
  const response = await api.delete(`/listening/brands/${brandId}`, withProjectHeader(projectId));
  return response.data;
};

const refreshBrandMentions = async (projectId: string, brandId: string, useAsync: boolean = true) => {
  const response = await api.post(`/listening/brands/${brandId}/refresh?async=${useAsync}`, {}, withProjectHeader(projectId));
  return response.data;
};

const getMentions = async (projectId: string, filters: MentionFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });

  const response = await api.get(`/listening/mentions?${params.toString()}`, withProjectHeader(projectId));
  return response.data;
};

const markAsResponded = async (projectId: string, mentionId: string) => {
  const response = await api.post(`/listening/mentions/${mentionId}/respond`, {}, withProjectHeader(projectId));
  return response.data;
};

const getStats = async (projectId: string, dateRange?: { start_date?: string; end_date?: string }) => {
  const params = new URLSearchParams();
  if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
  if (dateRange?.end_date) params.append('end_date', dateRange.end_date);

  const response = await api.get(`/listening/stats?${params.toString()}`, withProjectHeader(projectId));
  return response.data;
};

// Initialize brands from BusinessProfile (auto-populate from project settings)
const initializeFromProfile = async (projectId: string, replace: boolean = false) => {
  const response = await api.post(`/listening/brands/initialize?replace=${replace}`, {}, withProjectHeader(projectId));
  return response.data;
};

// Get BusinessProfile data for pre-filling the form
const getBusinessProfile = async (projectId: string) => {
  const response = await api.get(`/onboarding/profile`, withProjectHeader(projectId));
  return response.data;
};

export const listeningService = {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand,
  refreshBrandMentions,
  getMentions,
  markAsResponded,
  getStats,
  initializeFromProfile,
  getBusinessProfile
};

export default listeningService;
